import { AIFunction, CustomObject } from '../types';
import { DEFAULT_ALLOWED_MODELS } from '../config/modelConfig';
import { convertFieldsForBackend } from './typeMappers';
export interface CompiledFunctionData {
  function: {
    name: string;
    display_name: string;
    description?: string;
    instructions: string;
    input_types: string[];
    allowed_models: string[];
    response_format?: any;
  };
  // Keep both keys for backward/forward compatibility
  return_object?: {
    name: string;
    display_name: string;
    description?: string;
    fields: Array<{
      name: string;
      display_name: string;
      type: string;
      is_required: boolean;
      is_array: boolean;
      description?: string;
      allowed_values?: string[];
      array_item_type?: string;
    }>;
  };
  returnObject?: {
    name: string;
    display_name: string;
    description?: string;
    fields: Array<{
      name: string;
      display_name: string;
      type: string;
      is_required: boolean;
      is_array: boolean;
      description?: string;
      allowed_values?: string[];
      array_item_type?: string;
    }>;
  };
  metadata: {
    current_version: number;
    customer_id?: string;
    created_at?: string;
    updated_at?: string;
  };
  initialPrompt?: string;
}

export interface ImprovedFunctionData {
  name: string;
  function_name?: string; // AI might return this instead of 'name'
  display_name: string;
  description: string;
  instructions: string;
  input_types: string[];
  allowed_models: string[];
  response_type: 'text' | 'object' | 'list';
  response_format?: any;
  return_object?: {
    name: string;
    display_name: string;
    description: string;
    fields: Array<{
      name: string;
      display_name: string;
      type: string;
      is_required: boolean;
      is_array: boolean;
      description: string;
      allowed_values?: string[];
      array_item_type?: string;
    }>;
  };
  enable_google_search?: boolean;
  improvements_summary: string;
  confidence_score?: number;
}

// Valid input types from backend
const VALID_INPUT_TYPES = [
  'text', 'json', 'image', 'video', 'audio', 'mp3', 
  'pdf', 'csv', 'xml', 'html', 'markdown', 'binary'
];

// Map common invalid input types to valid ones
const mapInputType = (inputType: string): string => {
  const lowerType = inputType.toLowerCase();
  
  // Map common variations
  const typeMapping: Record<string, string> = {
    'file': 'binary',
    'files': 'binary',
    'document': 'pdf',
    'documents': 'pdf',
    'doc': 'pdf',
    'docx': 'pdf',
    'spreadsheet': 'csv',
    'excel': 'csv',
    'xls': 'csv',
    'xlsx': 'csv',
    'picture': 'image',
    'photo': 'image',
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'movie': 'video',
    'mp4': 'video',
    'avi': 'video',
    'sound': 'audio',
    'music': 'audio',
    'wav': 'audio',
    'webpage': 'html',
    'website': 'html',
    'markup': 'markdown',
    'md': 'markdown'
  };
  
  // Check if it's already valid
  if (VALID_INPUT_TYPES.includes(lowerType)) {
    return lowerType;
  }
  
  // Check mapping
  if (typeMapping[lowerType]) {
    return typeMapping[lowerType];
  }
  
  // Default to binary for unknown types
  return 'binary';
};

// Validate and fix input types array
const validateInputTypes = (inputTypes: string[] | undefined): string[] => {
  if (!inputTypes || !Array.isArray(inputTypes) || inputTypes.length === 0) {
    return ['text'];
  }
  
  return inputTypes.map(type => mapInputType(type));
};

// Commented out - not currently used but may be needed in future
// const ALLOWED_MODEL_TAGS = new Set([
//   'gpt35t','gpt-4o','gpt-4o-mini','o3-mini','gpt-4.1','gpt-4.1-mini','o4-mini',
//   'gemini','gemini-experimental','gemini-2','gemini-pro','gemini-2-5-flash','gemini-2-5-pro',
//   'groq-gemma','groq-llama','cerebras-8b','cerebras-70b','openai-large','openai-small',
//   'gemini-embeddings','grok2','command-r','command-r-plus','dall-e-2','dall-e-3',
//   'stable-diffusion-xl','midjourney-v6','whisper-large-v3'
// ]);
// const MODEL_NORMALIZE: Record<string, string> = {
//   'gpt-4-turbo': 'gpt-4o',
//   'gpt-4o': 'gpt-4o',
//   'gpt-4o-mini': 'gpt-4o-mini',
//   'gpt-4.1': 'gpt-4.1',
//   'gpt-4.1-mini': 'gpt-4.1-mini',
//   'gpt-3.5-turbo': 'gpt35t',
//   'gpt-3.5-turbo-0125': 'gpt35t',
//   'claude-3-opus-20240229': 'openai-large',
//   'gemini-1.5-pro-latest': 'gemini-pro',
//   'gemini-1.5-flash': 'gemini',
//   'gemini-2-5-flash': 'gemini-2-5-flash',
//   'gemini-2-5-pro': 'gemini-2-5-pro',
// };
// Commented out - not currently used but may be needed in future
// const normalizeAllowedModels = (models?: string[]) => {
//   const out: string[] = [];
//   (models || []).forEach((m) => {
//     const mapped = MODEL_NORMALIZE[m] || m;
//     if (ALLOWED_MODEL_TAGS.has(mapped) && !out.includes(mapped)) {
//       out.push(mapped);
//     }
//   });
//   if (out.length === 0) return [DEFAULT_MODEL];
//   return out;
// };

/**
 * Helper to check if a function name already exists for the current customer
 */
async function checkFunctionNameExists(
  name: string,
  idToken: string
): Promise<boolean> {
  try {
    // Try to fetch functions with exact name match
    const response = await fetch(
      `/api/v2/ai-functions/?name=${encodeURIComponent(name)}&include_public=false`, 
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      const functions = data.items || data.results || [];
      return functions.some((f: any) => f.name === name);
    }
    
    return false;
  } catch (error) {

    // Return true to be safe and avoid creating duplicates
    return true;
  }
}

/**
 * Helper to check if an object name already exists for the current customer
 */
async function checkObjectNameExists(
  name: string,
  idToken: string
): Promise<boolean> {
  try {
    // Try to fetch objects with exact name match
    const response = await fetch(
      `/api/v2/custom-objects/?name=${encodeURIComponent(name)}&include_public=true`, 
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      const objects = data.results || data.data || [];
      return objects.some((o: any) => o.name === name);
    }
    
    return false;
  } catch (error) {

    // Return true to be safe and avoid creating duplicates
    return true;
  }
}

// Helper: create custom object with retry on name/table collision
async function createObjectSafe(
  objectDef: any,
  idToken: string
): Promise<string> {
  // Use the centralized function that converts dot notation to nested structure for backend
  const fields = convertFieldsForBackend(objectDef.fields || []);

  // Backend sanitizes to snake_case; emulate a bit to avoid collisions
  const toSnake = (s: string) => (s || 'object')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'object';

  const baseName = toSnake(objectDef.name || objectDef.display_name || 'object');
  const baseDisplay = objectDef.display_name || objectDef.name || 'Object';

  for (let i = 0; i < 50; i++) {
    const suffix = i === 0 ? '' : `_${i + 1}`;
    const attemptName = `${baseName}${suffix}`;
    const attemptDisplay = `${baseDisplay}${i === 0 ? '' : ` ${i + 1}`}`;

    // Check if name already exists before trying to create
    const nameExists = await checkObjectNameExists(attemptName, idToken);
    if (nameExists) {

      continue;
    }

    const res = await fetch('/api/v2/custom-objects/', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: attemptName,
        display_name: attemptDisplay,
        description: objectDef.description,
        is_searchable: true,
        fields,
      }),
    });

    if (res.ok) {
      const data = await res.json();

      return data.data?.id || data.id;
    }

    const text = await res.text();
    // Collision messages to retry
    if (
      (res.status === 400 && /already exists/i.test(text)) ||
      (res.status === 500 && /duplicate key value/i.test(text))
    ) {

      continue; // try next suffix
    }
    throw new Error(`Failed to create object ${attemptName}: ${text || res.status}`);
  }

  throw new Error('Failed to create object: exhausted name retries');
}

/** Saves an AI-designed return object (renaming on collision) and returns its id and final name. */
export async function saveGeneratedReturnObject(
  objectDef: any,
  idToken: string
): Promise<{ id: string; name?: string }> {
  const id = await createObjectSafe(objectDef, idToken);
  const objectResponse = await fetch(`/api/v2/custom-objects/${id}`, {
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });
  if (!objectResponse.ok) return { id };
  const objectData = await objectResponse.json();
  return { id, name: objectData.data?.name || objectData.name };
}

/**
 * Fetches complete AI function data including its return object type
 */
export async function fetchCompleteFunctionData(
  functionId: string,
  idToken: string
): Promise<{ function: AIFunction; returnObject?: CustomObject }> {
  // Fetch the AI function details
  const functionResponse = await fetch(`/api/v2/ai-functions/${functionId}`, {
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!functionResponse.ok) {
    throw new Error('Failed to fetch function details');
  }

  const functionData = await functionResponse.json();
  const aiFunction = functionData.data || functionData;

  // Normalize response_format if stored as a string
  if (typeof (aiFunction as any).response_format === 'string') {
    try {
      (aiFunction as any).response_format = JSON.parse((aiFunction as any).response_format);
    } catch {
      // leave as-is if parsing fails
    }
  }

  // If the function returns an object, fetch the object details
  let returnObject: CustomObject | undefined;
  if (aiFunction.response_format?.object_id) {
    const objectResponse = await fetch(`/api/v2/custom-objects/${aiFunction.response_format.object_id}`, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (objectResponse.ok) {
      const objectData = await objectResponse.json();
      returnObject = objectData.data || objectData;
      
      // If the object doesn't have fields, fetch them separately
      if (returnObject && (!returnObject.fields || returnObject.fields.length === 0)) {
        const fieldsResponse = await fetch(`/api/v2/custom-objects/${aiFunction.response_format.object_id}/fields`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          returnObject.fields = fieldsData.data || fieldsData || [];
        }
      }
      
      // Now fetch any referenced objects for complete nested display
      if (returnObject && returnObject.fields) {
        const referencedObjectIds = new Set<string>();
        returnObject.fields.forEach((field: any) => {
          if (field.field_type === 'reference' && field.reference_object_id) {
            referencedObjectIds.add(field.reference_object_id);
          }
          if (field.field_type === 'array' && field.array_item_type === 'reference' && field.reference_object_id) {
            referencedObjectIds.add(field.reference_object_id);
          }
        });
        
        // Fetch all referenced objects
        const refObjectMap = new Map<string, any>();
        for (const refId of Array.from(referencedObjectIds)) {
          try {
            const refResponse = await fetch(`/api/v2/custom-objects/${refId}`, {
              headers: {
                'Authorization': `Bearer ${idToken}`,
              },
            });
            if (refResponse.ok) {
              const refData = await refResponse.json();
              const refObj = refData.data || refData;
              
              // Ensure fields are loaded for referenced object too
              if (!refObj.fields || refObj.fields.length === 0) {
                const refFieldsResponse = await fetch(`/api/v2/custom-objects/${refId}/fields`, {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                  },
                });
                if (refFieldsResponse.ok) {
                  const refFieldsData = await refFieldsResponse.json();
                  refObj.fields = refFieldsData.data || refFieldsData || [];
                }
              }
              
              refObjectMap.set(refId, refObj);
            }
          } catch (err) {

          }
        }
        
        // Attach the referenced objects to their fields
        returnObject.fields = returnObject.fields.map((field: any) => {
          if (field.reference_object_id && refObjectMap.has(field.reference_object_id)) {
            return {
              ...field,
              reference_object: refObjectMap.get(field.reference_object_id)
            };
          }
          return field;
        });
      }
    }
  }

  return { function: aiFunction, returnObject };
}

/**
 * Compiles all function data into a format suitable for LLM improvement
 */
export function compileFunctionDataForImprovement(
  aiFunction: Pick<AIFunction, 'name' | 'instructions' | 'input_types' | 'allowed_models'> &
    Partial<Pick<AIFunction, 'display_name' | 'description' | 'response_format' | 'version' | 'customer_id' | 'created_at' | 'updated_at'>>,
  returnObject?: Pick<CustomObject, 'name' | 'display_name' | 'description'> & { fields: any[] }
): CompiledFunctionData {
  const compiledData: CompiledFunctionData = {
    function: {
      name: aiFunction.name,
      display_name: aiFunction.display_name || aiFunction.name,
      description: aiFunction.description,
      instructions: aiFunction.instructions,
      input_types: aiFunction.input_types,
      allowed_models: aiFunction.allowed_models,
      response_format: aiFunction.response_format,
    },
    metadata: {
      current_version: aiFunction.version || 1,
      customer_id: aiFunction.customer_id,
      created_at: aiFunction.created_at,
      updated_at: aiFunction.updated_at,
    },
  };

  // Add return object details if available
  if (returnObject) {
    const objPayload = {
      name: returnObject.name,
      display_name: returnObject.display_name,
      description: returnObject.description,
      fields: returnObject.fields.map((field: any) => ({
        name: field.name,
        display_name: field.display_name,
        // Ensure we map backend field_type -> type for pydantic formatting
        field_type: field.field_type || field.type,
        type: field.type || field.field_type,
        is_required: field.is_required,
        is_array: field.is_array,
        description: field.description,
        allowed_values: field.allowed_values,
        array_item_type: field.array_item_type,
        reference_object_id: field.reference_object_id,
        reference_object_name: field.reference_object_name,
        // Include the full reference object if it was fetched
        reference_object: field.reference_object
      })),
    };
    // set both keys
    (compiledData as any).return_object = objPayload;
    (compiledData as any).returnObject = objPayload;
  }

  return compiledData;
}

/**
 * Sends compiled function data to LLM for improvement
 */
export async function improveWithAI(
  compiledData: CompiledFunctionData,
  idToken: string,
  additionalContext?: string,
  selectedModel?: string,
  selectedInputType: string = 'text'
): Promise<ImprovedFunctionData> {
  // Prepare the prompt for the improvement function
  const isNewFunction = !compiledData?.function?.name || compiledData?.function?.name === '';
  
  // For new functions, prioritize compiledData.initialPrompt over additionalContext
  const userRequirements = isNewFunction 
    ? (compiledData?.initialPrompt || additionalContext || 'No specific requirements provided')
    : (additionalContext || 'No specific instructions provided');
  

  
  const prompt = isNewFunction ? `
${userRequirements}
  `.trim() : `
User's improvement request:
${additionalContext || 'No specific instructions provided'}

Current function definition:
${JSON.stringify(compiledData, null, 2)}
  `.trim();

  // Call the public improve_aifunction endpoint
  const response = await fetch('/api/v2/functions/public/improve_aifunction', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    // Without a model the public function runs on its own first allowed model (set by admins).
    body: JSON.stringify({
      input: prompt,
      input_type: selectedInputType,
      ...(selectedModel ? { model: selectedModel } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to improve function with AI');
  }

  const data = await response.json();
  
  // The API returns just the result data directly
  // Validate that we got a valid result with expected improve-aifunction fields
  if (!data || typeof data !== 'object') {

    throw new Error(`Invalid response from improve_aifunction: expected object, got ${typeof data}`);
  }
  
  // Check if this looks like an error response (has error field but no function fields)
  if (data.error && !data.function_name && !data.instructions && !data.allowed_models) {
    const errorMessage = data.error;

    throw new Error(`Failed to improve function: ${errorMessage}`);
  }
  
  // Use the data directly as the result (API returns result data in body)
  const result = data;
  
  // If the result contains a return_object, fetch any referenced objects for complete display
  if (result.return_object && result.return_object.fields) {
    const referencedObjectIds = new Set<string>();
    
    // First, we need to fetch public objects that might be referenced
    // Fetch the list of public objects to map names to IDs
    const publicObjectsResponse = await fetch('/api/v2/custom-objects/?include_public=true&customer_filter=public_only', {
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    
    const nameToIdMap = new Map<string, string>();
    if (publicObjectsResponse.ok) {
      const publicObjectsData = await publicObjectsResponse.json();
      const objects = publicObjectsData.results || publicObjectsData.data || [];
      objects.forEach((obj: any) => {
        nameToIdMap.set(obj.name, obj.id);
      });
    }
    
    // Collect referenced object names/ids
    result.return_object.fields.forEach((field: any) => {
      if (field.field_type === 'reference' && field.reference_object_name) {
        const refId = nameToIdMap.get(field.reference_object_name);
        if (refId) {
          referencedObjectIds.add(refId);
          field.reference_object_id = refId;
        }
      }
      if (field.field_type === 'array' && field.array_item_type === 'reference' && field.reference_object_name) {
        const refId = nameToIdMap.get(field.reference_object_name);
        if (refId) {
          referencedObjectIds.add(refId);
          field.reference_object_id = refId;
        }
      }
    });
    
    // Fetch all referenced objects
    const refObjectMap = new Map<string, any>();
    for (const refId of Array.from(referencedObjectIds)) {
      try {
        const refResponse = await fetch(`/api/v2/custom-objects/${refId}`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (refResponse.ok) {
          const refData = await refResponse.json();
          const refObj = refData.data || refData;
          
          // Ensure fields are loaded for referenced object too
          if (!refObj.fields || refObj.fields.length === 0) {
            const refFieldsResponse = await fetch(`/api/v2/custom-objects/${refId}/fields`, {
              headers: {
                'Authorization': `Bearer ${idToken}`,
              },
            });
            if (refFieldsResponse.ok) {
              const refFieldsData = await refFieldsResponse.json();
              refObj.fields = refFieldsData.data || refFieldsData || [];
            }
          }
          
          refObjectMap.set(refId, refObj);
        }
      } catch (err) {

      }
    }
    
    // Attach the referenced objects to their fields
    result.return_object.fields = result.return_object.fields.map((field: any) => {
      if (field.reference_object_id && refObjectMap.has(field.reference_object_id)) {
        return {
          ...field,
          reference_object: refObjectMap.get(field.reference_object_id)
        };
      }
      return field;
    });
  }
  
  // Parse the improved function response
  return result;
}

/**
 * Creates a new function and object from improved data
 */
export async function createImprovedFunction(
  improvedData: ImprovedFunctionData,
  idToken: string,
  originalFunctionId: string,
  options?: {
    /** Force the function name (keeps name-based lookups stable when regenerating). */
    overrideName?: string;
    /** Carried into metadata so a regenerated version keeps its knowledge config. */
    knowledgeConfig?: {
      categoryId?: string;
      knowledgeChunks?: number;
    };
    /** Stored in metadata for traceability when generated from a fresh prompt. */
    generationPrompt?: string;
    /** Defaults to '[AI Improved]'. */
    descriptionPrefix?: string;
    /**
     * Model pins for the new version. Omit to inherit the previous version's list,
     * in its existing order — the update route falls back to `existing.allowed_models`
     * when this is absent. Only pass a value when the user deliberately changed it.
     */
    allowedModels?: string[];
  }
): Promise<{ functionId: string; objectIds: string[] }> {
  const createdObjects: { name: string; id: string }[] = [];
  
  // Create the main return object if it exists
  let mainObjectId: string | undefined;
  let actualMainObjectName: string | undefined;
  
  if (improvedData.return_object) {
    const saved = await saveGeneratedReturnObject(improvedData.return_object, idToken);
    mainObjectId = saved.id;
    actualMainObjectName = saved.name;
    if (actualMainObjectName) {
      createdObjects.push({ name: actualMainObjectName, id: mainObjectId });
    }
  }

  // Normalize allowed model tags to backend enum
  // Commented out - not currently used but may be needed in future
  // const ALLOWED_MODEL_TAGS = new Set([
  //   'gpt35t','gpt-4o','gpt-4o-mini','o3-mini','gpt-4.1','gpt-4.1-mini','o4-mini',
  //   'gemini','gemini-experimental','gemini-2','gemini-pro','gemini-2-5-flash','gemini-2-5-pro',
  //   'groq-gemma','groq-llama','cerebras-8b','cerebras-70b','openai-large','openai-small',
  //   'gemini-embeddings','grok2','command-r','command-r-plus','dall-e-2','dall-e-3',
  //   'stable-diffusion-xl','midjourney-v6','whisper-large-v3'
  // ]);
  // const MODEL_NORMALIZE: Record<string, string> = {
  //   'gpt-4-turbo': 'gpt-4o',
  //   'gpt-4o': 'gpt-4o',
  //   'gpt-4o-mini': 'gpt-4o-mini',
  //   'gpt-4.1': 'gpt-4.1',
  //   'gpt-4.1-mini': 'gpt-4.1-mini',
  //   'gpt-3.5-turbo': 'gpt35t',
  //   'gpt-3.5-turbo-0125': 'gpt35t',
  //   'claude-3-opus-20240229': 'openai-large',
  //   'gemini-1.5-pro-latest': 'gemini-pro',
  //   'gemini-1.5-flash': 'gemini',
  //   'gemini-2-5-flash': 'gemini-2-5-flash',
  //   'gemini-2-5-pro': 'gemini-2-5-pro',
  // };
  // Commented out - not currently used but may be needed in future
  // const normalizeAllowedModels = (models?: string[]) => {
  //   const out: string[] = [];
  //   (models || []).forEach((m) => {
  //     const mapped = MODEL_NORMALIZE[m] || m;
  //     if (ALLOWED_MODEL_TAGS.has(mapped) && !out.includes(mapped)) {
  //       out.push(mapped);
  //     }
  //   });
  //   if (out.length === 0) return ['gemini-2-5-flash'];
  //   return out;
  // };

  // Update the existing function (creates a new version)
  const functionResponse = await fetch(`/api/v2/ai-functions/${originalFunctionId}?create_version=true`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: options?.overrideName || improvedData.function_name || improvedData.name,
      display_name: improvedData.display_name,
      description: `${options?.descriptionPrefix || '[AI Improved]'} ${improvedData.description}`,
      instructions: improvedData.instructions,
      input_types: validateInputTypes(improvedData.input_types),
      // Left out unless the user edited it, so the new version keeps whatever models the
      // previous one was pinned to. Sending a default here used to silently wipe pins.
      ...(options?.allowedModels?.length ? { allowed_models: options.allowedModels } : {}),
      response_type: improvedData.response_type,
      response_format: mainObjectId ? {
        type: improvedData.response_type === 'list' ? 'array' : 'object',
        object_id: mainObjectId,
        object_name: actualMainObjectName || improvedData.return_object?.name,
        is_list: improvedData.response_type === 'list',
      } : undefined,
      metadata: {
        improved_from: originalFunctionId,
        improvements_summary: improvedData.improvements_summary,
        ai_generated: true,
        ...(options?.generationPrompt ? { generation_prompt: options.generationPrompt } : {}),
        ...(improvedData.enable_google_search ? { enable_google_search: true } : {}),
        ...(options?.knowledgeConfig?.categoryId && {
          knowledge_categories: [options.knowledgeConfig.categoryId],
          knowledge_chunks: options.knowledgeConfig.knowledgeChunks || 2,
        }),
      },
    }),
  });

  if (!functionResponse.ok) {
    const error = await functionResponse.text();
    throw new Error(`Failed to create improved function: ${error}`);
  }

  const functionData = await functionResponse.json();
  const functionId = functionData.data?.id || functionData.id;

  return { functionId, objectIds: createdObjects.map(o => o.id) };
}

/**
 * Creates a brand new function (and objects) from improved data (no original function needed)
 */
// Track ongoing function creations to prevent duplicates
const ongoingFunctionCreations = new Set<string>();

export async function createNewFunction(
  improvedData: ImprovedFunctionData,
  idToken: string,
  knowledgeConfig?: {
    categoryId?: string;
    knowledgeChunks?: number;
  },
  generationPrompt?: string,
  /** Model pins. There is no previous version to inherit from, so this defaults to DEFAULT_ALLOWED_MODELS. */
  allowedModels?: string[]
): Promise<{ functionId: string; objectIds: string[] }> {
  // Get the function name from either function_name or name field
  const functionName = improvedData.function_name || improvedData.name || 'unnamed_function';
  
  // Check if this function name is already being created
  if (ongoingFunctionCreations.has(functionName)) {

    throw new Error('Function creation already in progress. Please wait and try again.');
  }
  
  ongoingFunctionCreations.add(functionName);
  
  try {
    const createdObjects: { name: string; id: string }[] = [];

  let mainObjectId: string | undefined;
  let actualMainObjectName: string | undefined;
  
  if (improvedData.return_object) {
    const saved = await saveGeneratedReturnObject(improvedData.return_object, idToken);
    mainObjectId = saved.id;
    actualMainObjectName = saved.name;
    if (actualMainObjectName) {
      createdObjects.push({ name: actualMainObjectName, id: mainObjectId });
    }
  }

  // We'll check function names on-demand as we try them


  // Ensure unique function name within customer
  // Convert to snake_case to match backend expectations
  const toSnakeCase = (str: string) => {
    return str
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_') || 'function';
  };
  
  let finalFunctionName = toSnakeCase(functionName);
  let functionCreated = false;
  let functionId: string | undefined;
  
  // Try up to 50 suffixes
  for (let i = 0; i < 50; i++) {
    const attemptName = i === 0 ? finalFunctionName : `${finalFunctionName}_${i + 1}`;
    const attemptDisplayName = i === 0 
      ? (improvedData.display_name || functionName)
      : `${improvedData.display_name || functionName} ${i + 1}`;
    
    // Check if name already exists before attempting to create
    const nameExists = await checkFunctionNameExists(attemptName, idToken);
    if (nameExists) {

      continue;
    }
    
    const functionResponse = await fetch('/api/v2/ai-functions/', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: attemptName,
        display_name: attemptDisplayName,
        description: `[AI Generated] ${improvedData.description || ''}`.trim(),
        instructions: improvedData.instructions,
        input_types: validateInputTypes(improvedData.input_types || ['text']),
        // Ignore whatever models the AI named; use the user's picks or our defaults.
        allowed_models: allowedModels?.length ? allowedModels : [...DEFAULT_ALLOWED_MODELS],
        response_format: mainObjectId ? {
          type: improvedData.response_type === 'list' ? 'array' : 'object',
          object_id: mainObjectId,
          object_name: actualMainObjectName || improvedData.return_object?.name,
          is_list: improvedData.response_type === 'list',
        } : undefined,
        metadata: {
          ai_generated: true,
          ...(generationPrompt ? { generation_prompt: generationPrompt } : {}),
          ...(improvedData.enable_google_search ? { enable_google_search: true } : {}),
          ...(knowledgeConfig?.categoryId && {
            knowledge_categories: [knowledgeConfig.categoryId],
            knowledge_chunks: knowledgeConfig.knowledgeChunks || 2,
          }),
        },
      }),
    });

    if (functionResponse.ok) {
      const functionData = await functionResponse.json();
      functionId = functionData.data?.id || functionData.id;
      functionCreated = true;

      break;
    } else {
      const error = await functionResponse.text();

      
      // Check if it's a name conflict error - check various status codes and error messages
      const isNameConflict = (
        // 400 Bad Request with specific messages
        (functionResponse.status === 400 && (
          error.toLowerCase().includes('already exists') || 
          error.toLowerCase().includes('duplicate') ||
          error.toLowerCase().includes('name already taken') ||
          error.toLowerCase().includes('name must be unique') ||
          error.toLowerCase().includes('constraint') ||
          error.toLowerCase().includes('violates unique')
        )) ||
        // 409 Conflict
        functionResponse.status === 409 ||
        // 422 Unprocessable Entity with name issues
        (functionResponse.status === 422 && error.toLowerCase().includes('name'))
      );
      
      if (isNameConflict) {

        // Try next suffix
        continue;
      } else {
        // Other error, throw
        throw new Error(`Failed to create function: ${error}`);
      }
    }
  }

  if (!functionCreated || !functionId) {
    throw new Error('Failed to create function: exhausted name attempts');
  }

  return { functionId, objectIds: createdObjects.map(o => o.id) };
  } finally {
    // Always clean up the tracking set
    ongoingFunctionCreations.delete(functionName);
  }
}

/**
 * Complete flow: fetch, improve, and create new function
 */
export async function autoImproveFunction(
  functionId: string,
  idToken: string,
  additionalContext?: string,
  onProgress?: (status: string) => void,
  selectedModel?: string
): Promise<{ functionId: string; objectId?: string }> {
  try {
    // Step 1: Fetch complete data
    onProgress?.('Fetching function data...');
    const { function: aiFunction, returnObject } = await fetchCompleteFunctionData(functionId, idToken);

    // Step 2: Compile data
    onProgress?.('Compiling function information...');
    const compiledData = compileFunctionDataForImprovement(aiFunction, returnObject);

    // Step 3: Send to AI for improvement
    onProgress?.('Analyzing with AI...');
    const selectedInputType = aiFunction?.input_types?.[0] || 'text';
    const improvedData = await improveWithAI(compiledData, idToken, additionalContext, selectedModel, selectedInputType);

    // Step 4: Create new function and object
    onProgress?.('Creating improved function...');
    const result = await createImprovedFunction(improvedData, idToken, functionId);

    onProgress?.('Improvement complete!');
    return result;
  } catch (error) {
    onProgress?.(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    throw error;
  }
}
